import { useEffect, useState } from "react";
import axios from "./api/axios";
import useAuth from "./hooks/useAuth";
import i18n from "./i18n";
import { defaultLanguage } from "./constants";

const WEBSITE_URL_ACC = "/dashboard";
const WEBSITE_URL_CONTACT = "/contact/key";

const StructuredData = () => {
  const { username } = useAuth();
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    if (!username) return;

    const fetchData = async () => {
      try {
        const art = defaultLanguage;
        const title = "accomodationForm";

        const accRes = await axios.get(WEBSITE_URL_ACC, {
          params: { username, art, title },
        });

        if (!accRes.data.entry) return;
        const entry = accRes.data.entry;

        const getContactField = async (key) => {
          const res = await axios.get(WEBSITE_URL_CONTACT, {
            params: { username, key },
          });
          return res.data[key];
        };

        const city = await getContactField("city");
        const country = await getContactField("country");
        const street = await getContactField("street");
        const number = await getContactField("number");
        const zip = await getContactField("zip");
        const tourismLicense = await getContactField("tourismLicense");
        const lat = await getContactField("positionLat");
        const lng = await getContactField("positionLng");

        const accTypeCode = entry.accomodationType;
        const accTypeLabel = i18n.t(`codes.acc_types.${accTypeCode}`);

        const hostname = window.location.hostname
          .replace(/^www\./, "")
          .split(".")[0];

        let googleRating = null;
        let reviewCount = null;
        let reviewArray = [];

        try {
          const googleRes = await axios.get(`google-reviews/${hostname}`);
          const data = googleRes.data;

          googleRating = data.rating;
          reviewCount = data.user_ratings_total || data.reviewCount;

          reviewArray =
            data.reviews?.map((r) => ({
              "@type": "Review",
              author: {
                "@type": "Person",
                name: r.author_name,
              },
              reviewRating: {
                "@type": "Rating",
                ratingValue: r.rating,
              },
              reviewBody: r.text,
              datePublished: new Date(r.time * 1000)
                .toISOString()
                .split("T")[0],
            })) || [];
        } catch (err) {
          console.error("Google reviews failed for schema");
        }

        let faqSchema = null;

        try {
            const faqRes = await axios.get("/websitejson", {
                params: { username, art:"en", title: "reviews-FAQ" },
            });

            if (faqRes.data.entry && faqRes.data.entry.content) {
                const faqContent = JSON.parse(faqRes.data.entry.content);

                if (Array.isArray(faqContent) && faqContent.length > 0) {
                faqSchema = {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: faqContent.map((item) => ({
                    "@type": "Question",
                    name: item.intro,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: item.text,
                    },
                    })),
                };
                }
            }
        } catch (err) {
            console.error("FAQ schema error:", err);
        }

        const vacationRentalSchema = {
            "@context": "https://schema.org",
            "@type": "VacationRental",

            name: entry.nameAccommodation || username,
            description: entry.description || undefined,

            image: [
                window.location.origin + "/Logo_light.svg",
            ],

            additionalType: accTypeLabel || undefined,

            address: {
                "@type": "PostalAddress",
                streetAddress: `${street} ${number}`,
                addressLocality: city,
                postalCode: zip,
                addressCountry: country,
            },

            geo: lat && lng ? {
                "@type": "GeoCoordinates",
                latitude: lat,
                longitude: lng,
            } : undefined,

            containsPlace: {
                "@type": "Accommodation",
                numberOfBedrooms: entry.bedrooms,
                numberOfBathroomsTotal: entry.bathrooms,
                occupancy: {
                    "@type": "QuantitativeValue",
                    maxValue: entry.guests
                }
            },

            minimumNights: entry.numNights,

            checkinTime: entry.checkIn,
            checkoutTime: entry.checkOut,

            offers: {
                "@type": "Offer",
                price: entry.lowerPrice,
                priceCurrency: entry.currency,
            },

            aggregateRating:
                googleRating && reviewCount
                ? {
                    "@type": "AggregateRating",
                    ratingValue: googleRating,
                    reviewCount: reviewCount,
                    }
                : undefined,

            review:
                googleRating && reviewCount && reviewArray.length > 0
                ? reviewArray
                : undefined,

            identifier: tourismLicense || undefined,
            };

        const organizationSchema = {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: entry.nameAccommodation || username,
            url: window.location.origin,
            logo: window.location.origin + "/Logo_light.svg",
        };

        const combinedSchema = [
            vacationRentalSchema,
            organizationSchema,
            ...(faqSchema ? [faqSchema] : []),
        ];


        const cleanSchema = JSON.parse(JSON.stringify(combinedSchema));
        setSchema(cleanSchema);
      } catch (error) {
        console.error("Structured data error:", error);
      }
    };

    fetchData();
  }, [username]);

  useEffect(() => {
    if (!schema) return;

    const existing = document.getElementById("structured-data");
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    script.innerHTML = JSON.stringify(schema);

    document.head.appendChild(script);
  }, [schema]);

  return null;
};

export default StructuredData;
